import {
	Component,
	computed,
	effect,
	inject,
	input,
	signal,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';

import { CtaType } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import { PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import {
	ContentData,
	SimpleHotelData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';

export interface FacilityFilter {
	facilities: { guid: string; name: string }[];
	facilityType: string;
}

@Component({
	selector: 'sb-facilities-hotels-list',
	templateUrl: './facilities-hotels-list.html',
	imports: [
		DynamicModule,
		StoryblokDirective,
		StoryblokSearchableListSectionComponent,
	],
})
export class StoryblokFacilitiesHotelsListComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly translate = inject(TranslateService);
	readonly bookingtoolUrl = inject(BOOKINGTOOL);

	title = input.required<string>();
	filters = input.required<FacilityFilter>();
	hideFilter = input<boolean>(false);

	emptyListTitle = input.required<string>();
	emptyListContent = input.required<string>();

	language: string = 'nl';

	private readonly hotelsData = signal<SimpleHotelData[]>([]);

	hotels = computed<ExtendedPersuadeCardInputs[]>(() => {
		return this.hotelsData().map((hotel) => ({
			title: hotel.name,
			content: this.transformContent(hotel.content),
			images: hotel.images,
			iconList: hotel.iconList ?? [],
			iconColor: hotel.iconColor,
			price: hotel.availability.isBookable && hotel.price,
			pricingToolTip: {
				occupancy: PRICING_TOOLTIP_OCCUPANCY,
			},
			socialProof: {
				score: hotel.rating.score,
				numberOfRatings: hotel.rating.numberOfRatings,
			},
			cardUrl: hotel.toUrl,
			secondaryCtaLinkType: 'url',
			secondaryCtaTarget: '_blank' as const,
			secondaryCtaText:
				hotel.availability.isBookable &&
				this.translate.instant('global.goto-hotel-cta'),
			secondaryCtaUrl: hotel.availability.isBookable ? hotel.toUrl : null,
			ctaUrl: hotel.availability.isBookable
				? BookingRedirectHelper.getBookingToolUrl(
						this.bookingtoolUrl,
						hotel.slug,
						{
							languageCode: this.language,
						},
					)
				: hotel.toUrl,
			ctaText: hotel.availability.isBookable
				? this.translate.instant('global.button-cta')
				: this.translate.instant('global.goto-hotel-cta'),
			ctaTarget: '_blank' as const,
			ctaType: 'btn-secondary' as CtaType,
			showDisabledText: !hotel.isAvailable,
		}));
	});

	constructor() {
		super();
		this.language =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();

		effect(() => {
			const facilityGuids = this.filters().facilities.map(
				(facility) => facility.guid,
			);

			this.contentService
				.getHotelsByFacilities(
					this.filters().facilityType,
					facilityGuids,
					this.language,
				)
				.subscribe({
					next: (response) => {
						if (response) {
							this.hotelsData.set(response);
						} else {
							this.hotelsData.set([]);
						}
					},
					error: () => {
						this.hotelsData.set([]);
					},
				});
		});
	}

	private readonly transformContent = (content: ContentData) => {
		if (content?.type === 'array') {
			return content.value.map((item) => `- ${item}`).join('\n');
		}

		return '';
	};
}
